'use client';
import { useState } from 'react';
import { Box, Button, Flex, Input, Text } from '@chakra-ui/react';
import { PromptSubtopicEditor } from '@/components/shared/PromptSubtopicEditor';
import { InlineFilterGroup } from '@/components/shared/InlineFilterControls';
import type { IntelligenceSetupDraft } from '@/lib/onboarding/setup-draft';
import { draftRefs } from '@/lib/onboarding/topic-draft';
import { MAX_PROMPT_GROUPS, MAX_PROMPTS_PER_GROUP } from '@/lib/prompt-topics/contract';
export function TopicDraftEditor({
  draft,
  onChange,
  disabled,
}: {
  draft: IntelligenceSetupDraft;
  onChange: (patch: Partial<IntelligenceSetupDraft>) => void;
  disabled: boolean;
}) {
  const [selected, setSelected] = useState('');
  const [name, setName] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const groups = draft.promptGroups || [];
  const group = groups.find(g => g.name === selected);
  const refs = draftRefs(draft);
  const rows = draft.prompts
    .map((text, i) => ({ text, ref: refs[i] }))
    .filter(p => p.text.toLowerCase().includes(search.toLowerCase()));
  const visible = rows.slice(page * 50, (page + 1) * 50);
  const save = (members: string[]) =>
    onChange({
      promptRefs: refs,
      promptGroups: groups.map(g =>
        g === group
          ? {
              ...g,
              prompt_refs: members,
              ...(g.subtopics !== undefined
                ? {
                    subtopics: g.subtopics.map(child => ({
                      ...child,
                      prompt_refs: child.prompt_refs.filter(ref => members.includes(ref)),
                    })),
                  }
                : {}),
            }
          : g
      ),
    });
  const duplicate = groups.some(
    g => g !== group && g.name.toLowerCase() === name.trim().toLowerCase()
  );
  return (
    <Box borderWidth="1px" borderColor="ui.border.subtle" borderRadius="10px" p="3" mb="3">
      <Flex gap="2" flexWrap="wrap" align="center">
        <InlineFilterGroup
          label="Topic"
                hideLabel={false}
                compact={false}
          options={[
            { value: '', label: 'Create topic' },
            ...groups.map(g => ({ value: g.name, label: g.name })),
          ]}
          value={selected}
          onChange={v => {
            setSelected(v);
            setName(v);
            setPage(0);
          }}
          disabled={disabled}
        />
        <Input
          aria-label="Topic name"
          value={name}
          maxLength={80}
          onChange={e => setName(e.target.value)}
          size="sm"
          width="220px"
          disabled={disabled}
        />
        <Button
          size="xs"
          disabled={
            disabled || !name.trim() || duplicate || (!group && groups.length >= MAX_PROMPT_GROUPS)
          }
          onClick={() => {
            const next = name.trim();
            onChange({
              promptRefs: refs,
              promptGroups: group
                ? groups.map(g => (g === group ? { ...g, name: next } : g))
                : [...groups, { name: next, prompt_refs: [] }],
            });
            setSelected(next);
          }}
        >
          {group ? 'Rename' : 'Create'}
        </Button>
        {group ? (
          <Button
            size="xs"
            variant="ghost"
            disabled={disabled}
            onClick={() => {
              onChange({ promptGroups: groups.filter(g => g !== group) });
              setSelected('');
              setName('');
            }}
          >
            Remove topic
          </Button>
        ) : null}
      </Flex>
      {group ? (
        <>
          <Text fontSize="xs" my="2">
            {group.prompt_refs.length} / 250 prompts · {groups.length} / 50 topics. Removing a
            prompt also removes it from this topic’s subtopics.
          </Text>
          <Input
            aria-label="Search topic prompts"
            placeholder="Search prompts to assign"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(0);
            }}
            size="sm"
            mb="2"
          />
          <Flex gap="2" mb="2">
            <Button
              size="xs"
              disabled={
                disabled ||
                new Set([...group.prompt_refs, ...visible.map(p => p.ref)]).size >
                  MAX_PROMPTS_PER_GROUP
              }
              onClick={() => save([...new Set([...group.prompt_refs, ...visible.map(p => p.ref)])])}
            >
              Assign visible
            </Button>
            <Button
              size="xs"
              disabled={disabled}
              onClick={() => save(group.prompt_refs.filter(r => !visible.some(p => p.ref === r)))}
            >
              Unassign visible
            </Button>
          </Flex>
          <Box maxH="240px" overflowY="auto">
            {visible.map(p => (
              <label key={p.ref} style={{ display: 'flex', gap: 8, padding: '4px 0' }}>
                <input
                  type="checkbox"
                  checked={group.prompt_refs.includes(p.ref)}
                  disabled={
                    disabled ||
                    (!group.prompt_refs.includes(p.ref) && group.prompt_refs.length >= 250)
                  }
                  onChange={e =>
                    save(
                      e.target.checked
                        ? [...group.prompt_refs, p.ref]
                        : group.prompt_refs.filter(r => r !== p.ref)
                    )
                  }
                />
                <Text fontSize="sm">{p.text}</Text>
              </label>
            ))}
          </Box>
          <Flex gap="2" mt="2">
            <Button size="xs" disabled={!page} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Text fontSize="xs">
              {page + 1} / {Math.max(1, Math.ceil(rows.length / 50))}
            </Text>
            <Button
              size="xs"
              disabled={(page + 1) * 50 >= rows.length}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </Flex>
          <Box mt="3">
            <PromptSubtopicEditor
              key={group.name}
              createId={() => `child-${group.subtopics?.length || 0}`}
              subtopics={(group.subtopics || []).map((child, index) => ({
                id: `child-${index}`,
                name: child.name,
                prompt_ids: child.prompt_refs,
              }))}
              prompts={draft.prompts
                .map((text, index) => ({ text, prompt_id: refs[index] }))
                .filter(prompt => group.prompt_refs.includes(prompt.prompt_id))}
              disabled={disabled}
              onChange={children =>
                onChange({
                  promptGroups: groups.map(item =>
                    item === group
                      ? {
                          ...item,
                          subtopics: children.map(child => ({
                            name: child.name,
                            prompt_refs: child.prompt_ids,
                          })),
                        }
                      : item
                  ),
                })
              }
            />
          </Box>
        </>
      ) : (
        <Text fontSize="xs" mt="2">
          Create a topic, then assign prompts. Prompts may belong to multiple topics.
        </Text>
      )}
    </Box>
  );
}
